import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Plane, Calendar, CheckCircle, Clock, User, MapPin, ArrowRight, Send } from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700 border-amber-200', dot: 'bg-amber-500' },
  contacted: { label: 'Contacted', color: 'bg-blue-100 text-blue-700 border-blue-200', dot: 'bg-blue-500' },
  converted: { label: 'Converted', color: 'bg-green-100 text-green-700 border-green-200', dot: 'bg-green-500' },
};

export default function Dashboard() {
  const { user } = useAuth();
  const [interests, setInterests] = useState([]);
  const [loadingInterests, setLoadingInterests] = useState(true);

  useEffect(() => {
    axios.get(`${API}/travel-interest/my`)
      .then(res => setInterests(res.data))
      .catch(() => {})
      .finally(() => setLoadingInterests(false));
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-8"
          >
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2" data-testid="dashboard-title">Welcome back, {user?.name}!</h1>
            <p className="text-lg text-slate-600">Manage your flights and subscription</p>
          </motion.div>

          {/* Subscription Status */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mb-8"
          >
            <Card className="p-6 border-slate-200 bg-gradient-to-r from-slate-900 to-slate-800 text-white" data-testid="subscription-status-card">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <p className="text-amber-500 font-semibold mb-1">Your Subscription</p>
                  <h2 className="text-2xl font-bold mb-2">
                    {user?.subscription_status === 'active' 
                      ? `${user.subscription_plan?.toUpperCase()} Plan` 
                      : 'No Active Subscription'}
                  </h2>
                  {user?.subscription_status === 'active' && user?.subscription_end && (
                    <p className="text-slate-300">
                      Valid until: {new Date(user.subscription_end).toLocaleDateString('en-IN', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </p>
                  )}
                </div>
                {user?.subscription_status !== 'active' && (
                  <Link to="/subscription-plans" data-testid="dashboard-subscribe-btn">
                    <Button className="rounded-full bg-amber-600 hover:bg-amber-700 text-white px-8 py-6">
                      Subscribe Now
                    </Button>
                  </Link>
                )}
              </div>
            </Card>
          </motion.div>

          {/* Interest Form Tracker */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-8"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-slate-900">Your Interest Requests</h2>
              <Link to="/subscription-plans" data-testid="dashboard-new-interest-btn">
                <Button className="rounded-full bg-amber-600 hover:bg-amber-700 text-white px-6">
                  <Send className="w-4 h-4 mr-2" /> New Request
                </Button>
              </Link>
            </div>

            {loadingInterests ? (
              <Card className="p-8 border-slate-200 text-center">
                <p className="text-slate-500">Loading your requests...</p>
              </Card>
            ) : interests.length === 0 ? (
              <Card className="p-8 border-slate-200 text-center" data-testid="no-interests-card">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MapPin className="w-8 h-8 text-slate-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">No requests yet</h3>
                <p className="text-slate-500 mb-6">Submit your travel requirements and we'll create a customized plan for you</p>
                <Link to="/subscription-plans">
                  <Button className="rounded-full bg-amber-600 hover:bg-amber-700 text-white px-8 py-5">
                    Get Your Custom Plan <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </Card>
            ) : (
              <div className="space-y-4">
                {interests.map((interest, index) => {
                  const status = statusConfig[interest.status] || statusConfig.pending;
                  return (
                    <motion.div
                      key={interest.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <Card className="p-5 border-slate-200 hover:border-slate-300 transition-colors" data-testid={`interest-card-${interest.id}`}>
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center flex-shrink-0">
                              <Plane className="w-5 h-5 text-amber-400" />
                            </div>
                            <div>
                              <p className="font-semibold text-slate-900">
                                {interest.from_city} <ArrowRight className="inline w-4 h-4 text-amber-500" /> {interest.to_city}
                              </p>
                              <p className="text-sm text-slate-500">
                                {interest.number_of_tickets} ticket{interest.number_of_tickets > 1 ? 's' : ''} &middot; {interest.travel_frequency}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="text-xs text-slate-400">
                              {new Date(interest.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </span>
                            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${status.color}`} data-testid={`interest-status-${interest.id}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`}></span>
                              {status.label}
                            </span>
                          </div>
                        </div>
                        {interest.additional_notes && (
                          <p className="mt-3 text-sm text-slate-500 pl-14">Note: {interest.additional_notes}</p>
                        )}
                      </Card>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </motion.div>

          {/* Quick Actions */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <h2 className="text-2xl font-bold text-slate-900 mb-6">Quick Actions</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Link to="/book-flight" data-testid="dashboard-book-flight-link">
                <Card className="p-6 border-slate-200 hover-lift cursor-pointer group">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-slate-900 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Plane className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 mb-1">Book a Flight</h3>
                      <p className="text-sm text-slate-600">Search and book flights</p>
                    </div>
                  </div>
                </Card>
              </Link>

              <Link to="/my-bookings" data-testid="dashboard-bookings-link">
                <Card className="p-6 border-slate-200 hover-lift cursor-pointer group">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-sky-500 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Clock className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 mb-1">My Bookings</h3>
                      <p className="text-sm text-slate-600">View booking history</p>
                    </div>
                  </div>
                </Card>
              </Link>

              <Link to="/profile" data-testid="dashboard-profile-link">
                <Card className="p-6 border-slate-200 hover-lift cursor-pointer group">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-amber-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                      <User className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 mb-1">My Profile</h3>
                      <p className="text-sm text-slate-600">Update account details</p>
                    </div>
                  </div>
                </Card>
              </Link>
            </div>
          </motion.div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
